import { useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { gql } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match, P } from 'ts-pattern';
import { dayjs } from '@lgg/isomorphic/dayjs';
import {
  Contact,
  MetricRealtimeContactsFollowUpContactScoreReason,
  MetricRealtimeContactsToFollowUpEdge,
  Query,
  QueryMetricRealtimeContactsToFollowUpArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { whenDefined } from '@lgg/isomorphic/when-defined';
import { ContactLabel } from 'src/components/domain/contacts/contact-label';
import { ContactOptionsBottomDrawer } from 'src/components/domain/contacts/contact-options-bottom-drawer';
import { ListLoadingItem } from 'src/components/general/feedback/list-loading-item';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import {
  Card,
  CardBaseContent,
  CardContactStageIcon,
  CardInfoIconWithTooltip,
  CardListScrollOverlayIndicator,
  ContactAssignee,
  contactListItemContactLabelStyles,
  emptyValuePlaceholder,
} from 'src/components/pages/dashboard/components/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useInfiniteListQuery } from 'src/hooks/use-infinite-list-query';
import { useVisible } from 'src/hooks/use-visible';

export const GET_METRIC_CONTACTS_TO_FOLLOW_UP_QUERY = gql`
  query GetMetricRealtimeContactsToFollowUp(
    $institutionId: Int!
    $first: Int
    $after: String
    $where: MetricRealtimeContactsToFollowWhereInput
  ) {
    metricRealtimeContactsToFollowUp(
      institutionId: $institutionId
      first: $first
      after: $after
      where: $where
    ) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        endCursor
        startCursor
      }
      edges {
        cursor
        node {
          contact {
            id
            fullName
            label
            stage {
              slug
              name
            }
            assignee {
              id
              fullName
            }
            lastContactInteraction {
              id
              occurredAt
            }
            closedAt
            tags {
              id
              name
            }
          }
          reason
        }
      }
    }
  }
`;

const CardContent = styled(CardBaseContent)<{ $variant: 'agent' | 'regular' }>`
  height: 357px;

  ${({ $variant }) => {
    if ($variant === 'regular') {
      return css`
        ${up('md')} {
          height: 447px;
        }

        ${up('xl')} {
          height: 343px;
        }
      `;
    }
  }}
`;

const ContactToFollowUpListItemContainer = styled(FlexRow)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  gap: 10px;
  height: 60px;
  justify-content: space-between;
  margin: 0 20px;
  padding-top: 15px;
`;

const ContactToFollowUpLeftRail = styled(FlexRow)`
  overflow: hidden;
`;

const ContactInformationContainer = styled(FlexColumn)`
  margin-left: 10px;
  overflow: hidden;
`;

const LastContactInteraction = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 15px;
  text-align: right;
  white-space: nowrap;
`;

const ReasonToFollowUp = styled(FlexRow)`
  align-items: center;
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  justify-content: flex-end;
  line-height: 15px;
  white-space: nowrap;
`;

const ReasonInfoIcon = styled(CardInfoIconWithTooltip)`
  margin-left: 4px;

  svg {
    height: 12px;
    width: 12px;
  }
`;

const StyledContactLabel = styled(ContactLabel)`
  .contact-label-text-column {
    &,
    & > a {
      width: 100%;
    }

    .contact-label_text {
      ${contactListItemContactLabelStyles}
    }
  }
`;

const BottomListSpacer = styled.div`
  height: 30px;
`;

export type ContactToFollowUpListItemProps = {
  contact: Contact;
  reason: MetricRealtimeContactsFollowUpContactScoreReason;
};

export const ContactToFollowUpListItem = ({
  contact,
  reason,
}: ContactToFollowUpListItemProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['dashboard', 'conversations', 'common']);
  const { id, assignee, lastContactInteraction, closedAt } = contact;
  const optionsBottomDrawerVisibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));

  const reasonInfo = useMemo(() => {
    const formatDurationFromNow = (date: Date) => dayjs(date).locale(language).fromNow();

    const lastInteractionBaseInfo = {
      description: t('dashboard:cards.contactsToFollowUp.followUpReason.lastInteraction'),
      date: formatDurationFromNow(lastContactInteraction?.occurredAt as Date),
    };

    return (
      // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
      match(reason)
        .with('LEAD_NEW_HOT', () => ({
          description: t('dashboard:cards.contactsToFollowUp.followUpReason.newHotLead'),
          date: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonValues.withoutInteraction',
          ),
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.newHotLead',
          ),
        }))
        .with('LEAD_NEW', () => ({
          description: t('dashboard:cards.contactsToFollowUp.followUpReason.newLead'),
          date: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonValues.withoutInteraction',
          ),
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.newLead',
          ),
        }))
        .with('LEAD_INACTIVITY_DAYS_3_TO_7', () => ({
          ...lastInteractionBaseInfo,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.leadInactivity3To7Days',
          ),
        }))
        .with('LEAD_INACTIVITY_DAYS_7_TO_14', () => ({
          ...lastInteractionBaseInfo,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.leadInactivity7To14Days',
          ),
        }))
        .with('LEAD_INACTIVITY_DAYS_MORE_THAN_14', () => ({
          ...lastInteractionBaseInfo,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.leadInactivityMoreThan14Days',
          ),
        }))
        .with('LEAD_INACTIVITY_HOURS_24_TO_72', () => ({
          ...lastInteractionBaseInfo,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.leadInactivity24To72Hours',
          ),
        }))
        .with('OPPORTUNITY_INACTIVITY_DAYS_2_TO_7', () => ({
          ...lastInteractionBaseInfo,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.opportunityInactivity2To7Days',
          ),
        }))
        .with('OPPORTUNITY_INACTIVITY_DAYS_7_TO_14', () => ({
          ...lastInteractionBaseInfo,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.opportunityInactivity7To14Days',
          ),
        }))
        .with('OPPORTUNITY_INACTIVITY_DAYS_MORE_THAN_14', () => ({
          ...lastInteractionBaseInfo,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.opportunityInactivityMoreThan14Days',
          ),
        }))
        .with('CUSTOMER_SATISFACTION', () => ({
          description: t(
            'dashboard:cards.contactsToFollowUp.followUpReason.customerSatisfaction',
          ),
          date: `${t(
            'dashboard:cards.contactsToFollowUp.followUpReasonValues.closed',
          )} ${formatDurationFromNow(closedAt)}`,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.customerSatisfaction',
          ),
        }))
        .with('CUSTOMER_REPEATED_BUSINESS', () => ({
          description: t(
            'dashboard:cards.contactsToFollowUp.followUpReason.repeatedBusiness',
          ),
          date: `${t(
            'dashboard:cards.contactsToFollowUp.followUpReasonValues.closed',
          )} ${formatDurationFromNow(closedAt)}`,
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.repeatedBusiness',
          ),
        }))
        .with('BIRTHDATE', () => ({
          description: t('dashboard:cards.contactsToFollowUp.followUpReason.birthday'),
          date: t('common:pointInTime.today'),
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.birthday',
          ),
        }))
        .with('NEVER_INTERACTED_WITH', () => ({
          description: t(
            'dashboard:cards.contactsToFollowUp.followUpReason.lastInteraction',
          ),
          date: t('common:never'),
          tooltipDescription: t(
            'dashboard:cards.contactsToFollowUp.followUpReasonInfo.neverInteractedWidth',
          ),
        }))
        .exhaustive(() => ({
          description: '',
          date: '',
          tooltipDescription: '',
        }))
    );
  }, [closedAt, language, lastContactInteraction?.occurredAt, reason, t]);

  const assigneeInformation = useMemo(() => {
    const assigneeLabel = assignee ? assignee.fullName : emptyValuePlaceholder;

    return (
      <ContactAssignee
        name={assigneeLabel}
        testId="contacts-to-follow-up-assignee-name"
      />
    );
  }, [assignee]);

  return (
    <>
      <ContactToFollowUpListItemContainer
        data-lgg-id={`contact-to-follow-up-item-${id}`}
        onClick={!breakpointUpMd ? optionsBottomDrawerVisibilityHandler.show : undefined}
      >
        <ContactToFollowUpLeftRail>
          <CardContactStageIcon slug={contact.stage.slug} name={contact.label} />
          <ContactInformationContainer>
            <StyledContactLabel
              contact={contact}
              showStageIcon={false}
              testId="contact-to-follow-up-contact-name"
            />
            {assigneeInformation}
          </ContactInformationContainer>
        </ContactToFollowUpLeftRail>
        <FlexColumn>
          <ReasonToFollowUp data-lgg-id="contact-reason-to-follow-up">
            {reasonInfo.description}
            <ReasonInfoIcon
              type="infoAlertIcon"
              tooltipDescription={reasonInfo.tooltipDescription}
            />
          </ReasonToFollowUp>
          <LastContactInteraction data-lgg-id="contact-reason-to-follow-up-extra-info">
            {reasonInfo.date}
          </LastContactInteraction>
        </FlexColumn>
      </ContactToFollowUpListItemContainer>
      {!breakpointUpMd ? (
        <ContactOptionsBottomDrawer
          visible={optionsBottomDrawerVisibilityHandler.visible}
          onClose={optionsBottomDrawerVisibilityHandler.close}
          contact={contact}
        />
      ) : null}
    </>
  );
};

const PAGE_SIZE = 10;

type ContactsToFollowUpCardProps = {
  variant?: 'regular' | 'agent';
};

export const ContactsToFollowUpCard = ({
  variant = 'regular',
}: ContactsToFollowUpCardProps) => {
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const { t } = useTranslation(['dashboard']);
  const { targetUserId } = useContext(DashboardFiltersContext);

  const { loading, nodes, loadingMoreBottom, handleLoadBottom, firstItemIndex } =
    useInfiniteListQuery<
      Pick<Query, 'metricRealtimeContactsToFollowUp'>,
      Partial<QueryMetricRealtimeContactsToFollowUpArgs>,
      MetricRealtimeContactsToFollowUpEdge
    >({
      queryOptions: {
        query: GET_METRIC_CONTACTS_TO_FOLLOW_UP_QUERY,
        getNodeIdCallback: (template) => template?.contact.id,
        variables: {
          first: PAGE_SIZE,
          institutionId: currentInstitution.id,
          where: whenDefined(targetUserId, (id) => ({
            contactAssignee: {
              id: { _eq: id },
            },
          })),
        },
        getEdgesCallback: (data) => data?.metricRealtimeContactsToFollowUp.edges ?? [],
        getPageInfoCallback: (data) => data?.metricRealtimeContactsToFollowUp.pageInfo,
        onError: handleGraphQLError,
        queryPageSize: PAGE_SIZE,
      },
    });

  const listContent = useMemo(() => {
    return match({ loading, nodes })
      .with({ nodes: [] }, () => (
        <Card.EmptyState
          title={t('dashboard:cards.contactsToFollowUp.emptyState.title')}
          subTitle={t('dashboard:cards.contactsToFollowUp.emptyState.subTitle')}
          icon="contacts"
          testId="contacts-to-follow-up-empty"
        />
      ))
      .with({ nodes: P.when((nodes) => nodes.length > 0) }, ({ nodes }) => (
        <CardListScrollOverlayIndicator $showBottomShadow>
          <Virtuoso
            ref={virtuosoRef}
            data-lgg-id="contacts-to-follow-up-list"
            data={nodes}
            initialItemCount={0}
            initialTopMostItemIndex={0}
            endReached={async () => {
              await handleLoadBottom();
            }}
            increaseViewportBy={400}
            firstItemIndex={firstItemIndex}
            components={{
              Footer: () =>
                loadingMoreBottom ? (
                  <ListLoadingItem visible={loadingMoreBottom} />
                ) : (
                  <BottomListSpacer />
                ),
            }}
            itemContent={(_, { contact, reason }) => (
              <ContactToFollowUpListItem contact={contact} reason={reason} />
            )}
          />
        </CardListScrollOverlayIndicator>
      ))
      .run();
  }, [firstItemIndex, handleLoadBottom, loading, loadingMoreBottom, nodes, t]);

  const cardTitle = t('dashboard:cards.contactsToFollowUp.title');

  return (
    <Card loading={loading} data-lgg-id="dashboard-contacts-to-follow-up">
      <Card.Header
        title={cardTitle}
        cardInfoI18nKey="dashboard:cards.contactsToFollowUp.tooltipDescription"
        cardTextTitle={cardTitle}
      />
      <Card.Content>
        <CardContent $variant={variant} $isEmpty={nodes.length === 0}>
          {listContent}
        </CardContent>
      </Card.Content>
    </Card>
  );
};
